import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { projects, projectCategories } from "./projects.js";
import IndiaMap from "./IndiaMap.jsx";
import { motion, AnimatePresence } from "framer-motion";

export default function Projects() {
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(() => searchParams.get("category") || "all");

  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat) setFilter(cat);
  }, [searchParams]);
  const [view, setView] = useState("grid");
  const [sortBy, setSortBy] = useState("default");
  const [search, setSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  const parseYear = (period) => {
    if (!period) return 0;
    const match = period.match(/\d{4}/);
    return match ? parseInt(match[0], 10) : 0;
  };

  const filteredProjects = useMemo(() => {
    let filtered =
      filter === "all"
        ? projects
        : projects.filter((p) => p.category === filter);

    if (search.trim() !== "") {
      filtered = filtered.filter((p) =>
        p.title.toLowerCase().includes(search.toLowerCase())
      );
    }

    return filtered;
  }, [filter, search]);

  const sortedProjects = useMemo(() => {
    let sorted = [...filteredProjects];

    if (sortBy === "location") {
      sorted.sort((a, b) =>
        (a.location || "").localeCompare(b.location || "")
      );
    }

    if (sortBy === "dateNew") {
      sorted.sort((a, b) => parseYear(b.period) - parseYear(a.period));
    }

    if (sortBy === "dateOld") {
      sorted.sort((a, b) => parseYear(a.period) - parseYear(b.period));
    }

    return sorted;
  }, [filteredProjects, sortBy]);

  const getFirstImage = (project) => {
    return project.images && project.images.length > 0
      ? project.images[0]
      : "/project-images/default.jpg";
  };

  const nextImage = () => {
    if (!selectedProject?.images?.length) return;
    setActiveImage((prev) =>
      prev === selectedProject.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (!selectedProject?.images?.length) return;
    setActiveImage((prev) =>
      prev === 0 ? selectedProject.images.length - 1 : prev - 1
    );
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen">

      {/* CONTROL BAR */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-gray-200/70 border-b border-gray-300 shadow-md">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-6">

            {/* Row 1 on small: Search (full width) */}
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full md:max-w-md px-4 py-2 rounded-lg border border-gray-300 bg-white shadow-sm focus:ring-2 focus:ring-orange-500 focus:outline-none transition"
            />

            {/* Row 2 on small: Selects + Toggle */}
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="flex-1 min-w-[120px] px-3 py-2 rounded-lg border border-gray-300 bg-white shadow-sm text-sm focus:ring-2 focus:ring-orange-400"
              >
                {projectCategories.map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.title}
                  </option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 min-w-[120px] px-3 py-2 rounded-lg border border-gray-300 bg-white shadow-sm text-sm focus:ring-2 focus:ring-orange-400"
              >
                <option value="default">Sort</option>
                <option value="dateNew">Newest First</option>
                <option value="dateOld">Oldest First</option>
                <option value="location">Location A–Z</option>
              </select>

              <div className="flex bg-gray-300 rounded-full p-1 shadow-inner shrink-0 ml-auto md:ml-0">
                <button
                  onClick={() => setView("grid")}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                    view === "grid"
                      ? "bg-white shadow text-gray-900"
                      : "text-gray-600"
                  }`}
                >
                  Grid
                </button>

                <button
                  onClick={() => setView("map")}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                    view === "map"
                      ? "bg-white shadow text-gray-900"
                      : "text-gray-600"
                  }`}
                >
                  Map
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* GRID VIEW */}
      {view === "grid" ? (
        <motion.div
          layout
          className="max-w-7xl mx-auto px-6 mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10"
        >
          <AnimatePresence>
            {sortedProjects.map((project) => (
              <motion.div
                key={project.id}
                layout
                onClick={() => {
                  setSelectedProject(project);
                  setActiveImage(0);
                }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                whileHover={{ y: -6 }}
                className="cursor-pointer bg-white rounded-2xl shadow-md hover:shadow-2xl transition overflow-hidden"
              >
                {project.category !== "industrialWarehousing" && (
                  getFirstImage(project) ? (
                    <img
                      src={getFirstImage(project)}
                      alt={project.title}
                      className="w-full h-52 object-cover"
                    />
                  ) : (
                    <div className="relative w-full h-52 bg-gradient-to-br from-orange-500 to-orange-400 flex flex-col items-end justify-end p-5">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 text-white/20 absolute top-4 left-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <div className="flex flex-col items-end gap-2">
                        {project.type && (
                          <span className="text-white/80 text-[11px] font-medium bg-white/15 px-2.5 py-0.5 rounded-full">{project.type}</span>
                        )}
                        {project.value && (
                          <span className="text-white font-bold text-sm bg-white/20 px-2.5 py-1 rounded-full">{project.value}</span>
                        )}
                      </div>
                    </div>
                  )
                )}

                <div className={`p-6 ${project.category === "industrialWarehousing" ? "border-t-4 border-orange-400" : ""}`}>
                  {project.category === "industrialWarehousing" && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {project.type && (
                        <span className="text-[11px] font-medium text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100">{project.type}</span>
                      )}
                      {project.value && (
                        <span className="text-[11px] font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100">{project.value}</span>
                      )}
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {project.title}
                  </h3>

                  <p className="text-sm text-gray-500 mt-1">
                    {project.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 mt-14">
          <div className="bg-white rounded-2xl shadow-xl p-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              Project Distribution Across India
            </h2>
            <IndiaMap
              projects={sortedProjects}
              onSelectProject={(project) => {
                setSelectedProject(project);
                setActiveImage(0);
              }}
            />
          </div>
        </div>
      )}

      {/* MODAL */}
      <AnimatePresence>
        {selectedProject && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 50 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4"
              onClick={() => setSelectedProject(null)}
            >
              <div
                className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden relative"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Floating close button — adapts to image vs no-image */}
                <button
                  onClick={() => setSelectedProject(null)}
                  className={`absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 ${
                    selectedProject.images && selectedProject.images.length > 0
                      ? "bg-black/30 hover:bg-black/50 text-white backdrop-blur-sm"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-500"
                  }`}
                  aria-label="Close"
                >
                  ✕
                </button>

                {/* IMAGE SLIDER — only rendered when images exist */}
                {selectedProject.images && selectedProject.images.length > 0 && (
                  <div className="relative w-full h-64 bg-black">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={activeImage}
                        src={selectedProject.images[activeImage]}
                        alt={selectedProject.title}
                        className="w-full h-64 object-cover"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                      />
                    </AnimatePresence>

                    {selectedProject.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2
                                    w-12 h-12 rounded-full
                                    bg-white/20 backdrop-blur-3xl hover:bg-white/50
                                    text-white shadow-lg flex items-center justify-center
                                    transition-all duration-300"
                        >
                          <span className="text-3xl text-orange-500 flex items-center justify-center translate-y-[1px]">‹</span>
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2
                                    w-12 h-12 rounded-full
                                    bg-white/20 backdrop-blur-3xl hover:bg-white/50
                                    text-white shadow-lg flex items-center justify-center
                                    transition-all duration-300"
                        >
                          <span className="text-3xl text-orange-500 flex items-center justify-center translate-y-[1px]">›</span>
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                          {selectedProject.images.map((_, index) => (
                            <div
                              key={index}
                              onClick={() => setActiveImage(index)}
                              className={`w-3 h-3 rounded-full cursor-pointer ${activeImage === index ? "bg-orange-500" : "bg-white/70"}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* CONTENT */}
                <div className="p-8 overflow-y-auto custom-scrollbar">
                  {/* Accent bar shown only when there's no image */}
                  {(!selectedProject.images || selectedProject.images.length === 0) && (
                    <div className="-mx-8 -mt-8 mb-6 px-8 pt-8 pb-5 bg-gradient-to-r from-orange-500 to-orange-400 rounded-t-3xl">
                      <h2 className="text-xl font-bold text-white leading-snug pr-8">
                        {selectedProject.title}
                      </h2>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {selectedProject.location && (
                          <span className="inline-flex items-center gap-1 text-[11px] bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            {selectedProject.location}
                          </span>
                        )}
                        {selectedProject.value && (
                          <span className="inline-flex items-center gap-1 text-[11px] bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {selectedProject.value}
                          </span>
                        )}
                        {selectedProject.type && (
                          <span className="inline-flex items-center gap-1 text-[11px] bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                            {selectedProject.type}
                          </span>
                        )}
                        {selectedProject.period && (
                          <span className="inline-flex items-center gap-1 text-[11px] bg-white/20 text-white px-2.5 py-1 rounded-full font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            {selectedProject.period}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Title & standard metadata — shown for projects WITH images */}
                  {selectedProject.images && selectedProject.images.length > 0 && (
                    <>
                      <h2 className="text-2xl font-semibold text-gray-900 pr-6">
                        {selectedProject.title}
                      </h2>

                      <p className="text-gray-600 mt-3">
                        {selectedProject.location}
                      </p>

                      {selectedProject.period && (
                        <p className="text-orange-500 font-medium mt-2">
                          {selectedProject.period}
                        </p>
                      )}

                      {selectedProject.value && (
                        <p className="text-orange-600 font-semibold mt-2">
                          Project Cost: {selectedProject.value}
                        </p>
                      )}
                    </>
                  )}

                  {selectedProject.description && selectedProject.description.length > 0 ? (
                    selectedProject.description.map((para, index) => (
                      <p
                        key={index}
                        className="text-gray-700 mt-5 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: para }}
                      />
                    ))
                  ) : selectedProject.category !== "industrialWarehousing" ? (
                    <div className="mt-6 flex items-center gap-3 bg-orange-50 border border-orange-100 rounded-xl px-4 py-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-orange-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-orange-400 italic">Project details and description coming soon.</p>
                    </div>
                  ) : null}
                </div>

              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* FOOTER STRIP */}
      <div className="bg-orange-600 text-white text-center py-7 mt-20 tracking-[0.25em] text-[13px] uppercase" style={{ fontWeight: 600 }}>
        Disclaimer: All images used are illustrative and strictly for representational purposes only.
      </div>
    </div>
  );
}
